import type { ThemedToken } from 'shiki';

/**
 * Per-line diff classification produced by {@link applyDiffNotation}.
 */
export type DiffNotation = 'added' | 'deleted';

/**
 * A shiki token that may carry an additional flag identifying it as the diff
 * notation marker comment (e.g. the `// [!code ++]` portion of a line).
 *
 * Consumers (e.g. the editor's Slate decoration layer) use the flag to render
 * the marker dimmed so writers can still see and edit it.
 */
export interface NotationToken extends ThemedToken {
    hiddenMarker?: boolean;
}

export interface ApplyDiffNotationResult {
    tokens: NotationToken[][];
    /** `null` for lines without a marker; aligned 1:1 with `tokens`. */
    lineNotation: Array<DiffNotation | null>;
}

// Matches a trailing diff marker comment in any of the supported comment styles:
//   `// [!code ++]`        — JS/TS/Go/Rust/etc.
//   `# [!code ++]`         — Python/sh/Ruby
//   `-- [!code ++]`        — SQL/Lua/Haskell
//   `; [!code ++]`         — Lisp/INI
//   `<!-- [!code ++] -->`  — HTML/Markdown
//   `/* [!code ++] */`     — CSS/C
// The leading `[ \t]*` makes the match start at the whitespace immediately
// preceding the comment so the marker range covers that whitespace too.
const NOTATION_PATTERN =
    /[ \t]*(?:(?:\/\/|#|--|;)\s*\[!code\s+(\+\+|--)\]|<!--\s*\[!code\s+(\+\+|--)\]\s*-->|\/\*\s*\[!code\s+(\+\+|--)\]\s*\*\/)\s*$/;

/**
 * Detect whether a single raw line of code carries a trailing diff notation
 * marker. Cheap text-only check; useful when callers have plain text and
 * don't need a tokenized view (e.g. driving per-line tinting in editors).
 *
 * Why this is custom and not shiki's `transformerNotationDiff`: shiki's
 * transformer is HAST-only — it runs on `<line>` elements during HTML/HAST
 * construction, not on the `ThemedToken[][]` shape we consume. See the note
 * on {@link applyDiffNotation} for the full rationale.
 */
export function detectLineDiffNotation(line: string): DiffNotation | null {
    const match = line.match(NOTATION_PATTERN);
    if (!match) {
        return null;
    }
    const variant = match[1] ?? match[2] ?? match[3];
    return variant === '++' ? 'added' : 'deleted';
}

/**
 * Post-processes shiki's tokenized output to detect end-of-line diff notation
 * markers (the expressive-code / shiki `transformerNotationDiff` syntax) and
 * surface a per-line classification, while flagging the marker tokens with
 * `hiddenMarker: true` so consumers can render them dimmed.
 *
 * Why we reimplement this instead of using `@shikijs/transformers`:
 * `transformerNotationDiff` is built on shiki's `line` hook, which only fires
 * during HAST construction in `codeToHtml` / `codeToHast`. It does NOT run on
 * the `ThemedToken[][]` returned by `codeToTokens`. Our editor pipeline needs
 * tokens because Slate decorations are character ranges keyed off per-token
 * offsets — see `decorations.ts` and `tokens.ts` in the editor's
 * `code-syntax-plugin`. Switching to a HAST pipeline would refactor several
 * editor files for marginal cleanup. Until/unless we also need the other
 * notation transformers (`highlight`, `focus`, `error`, `warning`), keeping
 * this ~50-line utility is right-sized; the regex above mirrors what
 * `transformerNotationDiff` accepts for the `++`/`--` variants.
 */
export function applyDiffNotation(tokens: ThemedToken[][]): ApplyDiffNotationResult {
    const resultTokens: NotationToken[][] = [];
    const lineNotation: Array<DiffNotation | null> = [];

    for (const line of tokens) {
        const lineText = line.map((token) => token.content).join('');
        const match = lineText.match(NOTATION_PATTERN);

        if (!match || match.index === undefined) {
            resultTokens.push(line);
            lineNotation.push(null);
            continue;
        }

        const variant = match[1] ?? match[2] ?? match[3];
        const notation: DiffNotation = variant === '++' ? 'added' : 'deleted';
        const markerStart = match.index;
        const markerEnd = markerStart + match[0].length;

        resultTokens.push(flagMarkerTokens(line, markerStart, markerEnd));
        lineNotation.push(notation);
    }

    return { tokens: resultTokens, lineNotation };
}

/**
 * Walk a tokenized line and split any token overlapping `[markerStart, markerEnd]`
 * so the marker portion can be flagged with `hiddenMarker: true`. Tokens fully
 * outside the range are returned unchanged.
 */
function flagMarkerTokens(
    line: ThemedToken[],
    markerStart: number,
    markerEnd: number
): NotationToken[] {
    const result: NotationToken[] = [];
    let cursor = 0;

    for (const token of line) {
        const tokStart = cursor;
        const tokEnd = cursor + token.content.length;
        cursor = tokEnd;

        if (tokEnd <= markerStart || tokStart >= markerEnd) {
            result.push(token);
            continue;
        }

        const preLen = Math.max(0, markerStart - tokStart);
        const postLen = Math.max(0, tokEnd - markerEnd);
        const markerLen = token.content.length - preLen - postLen;

        if (preLen > 0) {
            result.push({ ...token, content: token.content.slice(0, preLen) });
        }
        if (markerLen > 0) {
            result.push({
                ...token,
                content: token.content.slice(preLen, preLen + markerLen),
                hiddenMarker: true,
            });
        }
        if (postLen > 0) {
            result.push({
                ...token,
                content: token.content.slice(preLen + markerLen),
            });
        }
    }

    return result;
}
